import { checkRevenueCenter } from '@sonifi/wb-in-room-dining/Settings/services/irdSettings.service';
import { GRANTS } from '@sonifi/wb-user/services/authorization.service';
import { NAV_KEYS } from '../../constants/wbNavConstants';
import { GROUP_DELIVERY_METHOD } from '@sonifi/wb-group/constants/wbGroupConstants';

const buildUrl = (url, params) => {
  const builtUrl = url.replace(/:(\w+)/g, (match, p1) => params[p1] || match);
  // Make sure the URL starts with "/" (absolute path)
  return builtUrl.startsWith('/') ? builtUrl : `/${builtUrl}`;
};

export const defaultProductsTab = {
  key: NAV_KEYS.products,
  title: 'Products',
  navChildren: [
    {
      key: NAV_KEYS.happenings,
      enabled: false,
      permissions: [GRANTS.ACCESS_GROUP_CHANNEL, GRANTS.ACCESS_HAPPENINGS],
      product: 'happenings_channel',
      url: '/products/:groupId/happenings',
      buildUrl: function ({ groupId, slideId }) {
        const url = buildUrl(this.url, { groupId });
        if (slideId) {
          return `${url}/slides/${slideId}`;
        }
        return url;
      },
    },
    {
      key: NAV_KEYS.hotelDirectory,
      enabled: false,
      permissions: [GRANTS.ACCESS_HOTEL_DIRECTORY],
      product: 'hotel_directory',
      url: '/products/:groupId/hotel_directory/:platform',
      buildUrl: function ({ groupId, platform }) {
        return buildUrl(this.url, { groupId, platform });
      },
    },
    {
      key: NAV_KEYS.inRoomDining,
      enabled: false,
      permissions: [GRANTS.ACCESS_IN_ROOM_DINING],
      product: 'ird',
      dispatchLoader:
        ({ groupId }) =>
        async (dispatch, getState) => {
          const {
            irdSettings: { revenueCenterSelected },
          } = getState();

          await dispatch(
            checkRevenueCenter({ groupId, revenueCenterSelected }),
          );
        },
      navChildren: [
        {
          key: NAV_KEYS.inRoomDiningMenu,
          permissions: [GRANTS.ACCESS_IN_ROOM_DINING],
          product: 'ird',
          title: 'Menu Builder',
          url: '/products/:groupId/in_room_dining/:revenueCenterId/menu/:platform',
          buildUrl: function ({ groupId, revenueCenterId, platform }) {
            return buildUrl(this.url, { groupId, revenueCenterId, platform });
          },
        },
        {
          key: NAV_KEYS.inRoomDiningSettings,
          permissions: [GRANTS.ACCESS_IN_ROOM_DINING],
          product: 'ird',
          title: 'Settings',
          url: '/products/:groupId/in_room_dining/:revenueCenterId/settings',
          buildUrl: function ({ groupId, revenueCenterId }) {
            return buildUrl(this.url, { groupId, revenueCenterId });
          },
        },
        {
          key: NAV_KEYS.inRoomDiningOrders,
          permissions: [GRANTS.ACCESS_IN_ROOM_DINING],
          product: 'ird',
          title: 'Orders',
          url: '/products/:groupId/in_room_dining/:revenueCenterId/orders',
          buildUrl: function ({ groupId, revenueCenterId, orderId }) {
            return buildUrl(this.url, { groupId, revenueCenterId, orderId });
          },
        },
      ],
    },
    {
      key: NAV_KEYS.reporting,
      enabled: false,
      permissions: [GRANTS.ACCESS_REPORTING],
      product: 'reporting',
      url: '/products/:groupId/reporting',
      buildUrl: function ({ groupId }) {
        return buildUrl(this.url, { groupId });
      },
    },
    {
      key: NAV_KEYS.guestRequest,
      enabled: false,
      permissions: [GRANTS.ACCESS_GUEST_REQUEST],
      product: 'guest_request',
      navChildren: [
        {
          key: NAV_KEYS.guestRequestMenu,
          permissions: [GRANTS.ACCESS_GUEST_REQUEST],
          product: 'guest_request',
          title: 'Content Manager',
          url: '/products/:groupId/guest_request/menu/:platform',
          buildUrl: function ({ groupId, platform }) {
            return buildUrl(this.url, { groupId, platform });
          },
        },
        {
          key: NAV_KEYS.guestRequestSettings,
          permissions: [GRANTS.ACCESS_GUEST_REQUEST],
          product: 'guest_request',
          title: 'Settings',
          url: '/products/:groupId/guest_request/settings',
          isVisible: (productSettings) =>
            !(
              productSettings.delivery_method ===
                GROUP_DELIVERY_METHOD.onSite && !productSettings.order_by_option
            ),
          buildUrl: function ({ groupId }) {
            return buildUrl(this.url, { groupId });
          },
        },
        {
          key: NAV_KEYS.guestRequestOrders,
          permissions: [GRANTS.ACCESS_GUEST_REQUEST],
          product: 'guest_request',
          title: 'Submitted Requests',
          url: '/products/:groupId/guest_request/orders?page=1&pageSize=100&sortBy=created&sortDir=desc',
          isVisible: (productSettings) =>
            productSettings.delivery_method !== GROUP_DELIVERY_METHOD.onSite,
          buildUrl: function ({ groupId }) {
            return buildUrl(this.url, { groupId });
          },
        },
      ],
    },
    {
      key: NAV_KEYS.branding,
      enabled: false,
      product: 'branding',
      permissions: [GRANTS.SONIFI_ADMIN],
      navChildren: [
        {
          key: NAV_KEYS.brandingGeneral,
          permissions: [GRANTS.SONIFI_ADMIN],
          product: 'branding',
          title: 'General',
          url: '/products/:groupId/branding/general',
          buildUrl: function ({ groupId }) {
            return buildUrl(this.url, { groupId });
          },
        },
        {
          key: NAV_KEYS.brandingStaycast,
          permissions: [GRANTS.SONIFI_ADMIN, GRANTS.ACCESS_STAYCAST],
          product: 'staycast',
          title: 'Staycast',
          url: '/products/:groupId/branding/staycast',
          buildUrl: function ({ groupId }) {
            return buildUrl(this.url, { groupId });
          },
        },
      ],
    },
  ],
};

export const defaultTabs = [
  { ...defaultProductsTab },
  {
    key: NAV_KEYS.admin,
    title: 'Admin',
    navChildren: [
      {
        key: NAV_KEYS.adminUsers,
        title: 'Users',
        url: '/users?page=1&pageSize=100&sortBy=email&sortDir=asc',
        permissions: [GRANTS.CREATE_USERS],
      },
      {
        key: NAV_KEYS.adminProperties,
        title: 'Properties',
        url: '/properties?page=1&pageSize=100&sortBy=cms_id&sortDir=asc',
        permissions: [GRANTS.SONIFI_ADMIN],
      },
      {
        key: NAV_KEYS.adminJobs,
        title: 'Jobs',
        url: '/jobs?page=1&pageSize=100&sortBy=id&sortDir=desc',
        permissions: [GRANTS.SONIFI_ADMIN],
      },
    ],
  },
  {
    key: NAV_KEYS.selfService,
    title: 'Self Service',
    permissions: [GRANTS.MANAGE_REMOTE_CONTROL_ORDERS],
    navChildren: [
      {
        key: NAV_KEYS.orderRemotes,
        title: 'Order Remotes',
        url: '/self_service/order_remotes',
        permissions: [GRANTS.MANAGE_REMOTE_CONTROL_ORDERS],
      },
    ],
  },
  {
    key: NAV_KEYS.helpAndTraining,
    title: 'Help and Training',
    navChildren: [
      {
        key: NAV_KEYS.helpAndTraining,
        title: 'Documentation and Videos',
        url: '/documentation',
      },
    ],
  },
];
